import type { BoardData, Connection, ConnectorType, Item, Port } from '../model/schema.js';
import { worldPosition } from '../model/areas.js';
import { round2 } from '../derive/helpers.js';
import { standOf } from '../stands/stands.js';
import { findPort, isTrunk, itemName, resolvePatch } from '../trace/ports.js';
import { roundToStock } from './config.js';
import type { CableEstimate, CableNeed, MaterialListConfig } from './types.js';

type Locale = 'en' | 'de';

const UNKNOWN: CableEstimate = { estimated: null, length: null };

/** Height of an item's connector above the floor: `z`, else the max height of its stand, else 0. */
export function itemHeight(data: BoardData, item: Item): number {
  return item.z ?? standOf(data, item)?.stand?.maxHeight ?? 0;
}

/**
 * Estimated cable length between two items (decision U11): horizontal distance plus the way down
 * from and up to each end's height, times the routing factor, plus the allowance at both ends;
 * rounded up to the next stock length. Unknown (null) if an end is missing or has no position (tray).
 */
export function estimateCableLength(
  from: Item | undefined,
  to: Item | undefined,
  data: BoardData,
  config: Pick<MaterialListConfig, 'cableAllowance' | 'stockLengths'>,
): CableEstimate {
  const a = from === undefined ? undefined : worldPosition(data, from);
  const b = to === undefined ? undefined : worldPosition(data, to);
  if (from === undefined || to === undefined || a === undefined || b === undefined) return UNKNOWN;
  const horizontal = Math.hypot(b.x - a.x, b.y - a.y);
  const vertical = itemHeight(data, from) + itemHeight(data, to);
  const { factor, perEnd } = config.cableAllowance;
  const estimated = round2((horizontal + vertical) * factor + 2 * perEnd);
  return { estimated, length: roundToStock(estimated, config.stockLengths) };
}

/** An entered length wins (used as is); otherwise the estimate between the two items. */
export function connectionLength(
  connection: Connection,
  from: Item | undefined,
  to: Item | undefined,
  data: BoardData,
  config: Pick<MaterialListConfig, 'cableAllowance' | 'stockLengths'>,
): CableEstimate {
  if (connection.length !== undefined) {
    return { estimated: connection.length, length: connection.length };
  }
  return estimateCableLength(from, to, data, config);
}

/** Cable names by connector (en/de). */
export const CABLE_NAMES: Record<Locale, Partial<Record<ConnectorType, string>>> = {
  en: {
    xlr3: 'XLR cable',
    xlr5: 'XLR 5-pin cable',
    trs: 'Jack cable (TRS)',
    ts: 'Instrument cable (jack)',
    rca: 'RCA cable',
    speakon: 'Speakon cable',
    ethercon: 'Network cable (etherCON)',
    rj45: 'Network cable (RJ45)',
    powercon: 'powerCON cable',
    powercon_true1: 'powerCON TRUE1 cable',
    schuko: 'Schuko extension',
    cee16: 'CEE16 extension',
    cee32: 'CEE32 extension',
    nema5_15: 'NEMA 5-15 extension',
    iec: 'IEC power cable',
    multipin: 'Multicore (multipin)',
  },
  de: {
    xlr3: 'XLR-Kabel',
    xlr5: 'XLR-Kabel 5-polig',
    trs: 'Klinkenkabel (TRS)',
    ts: 'Instrumentenkabel (Klinke)',
    rca: 'Cinch-Kabel',
    speakon: 'Speakon-Kabel',
    ethercon: 'Netzwerkkabel (etherCON)',
    rj45: 'Netzwerkkabel (RJ45)',
    powercon: 'powerCON-Kabel',
    powercon_true1: 'powerCON-TRUE1-Kabel',
    schuko: 'Schuko-Verlängerung',
    cee16: 'CEE16-Verlängerung',
    cee32: 'CEE32-Verlängerung',
    nema5_15: 'NEMA-5-15-Verlängerung',
    iec: 'Kaltgerätekabel',
    multipin: 'Multicore (Multipin)',
  },
};

/** Cable names by category when no connector is known. */
export const FALLBACK_NAMES: Record<Locale, Record<CableNeed['category'], string>> = {
  en: { cables_audio: 'Audio cable', cables_power: 'Power cable', cables_network: 'Network cable' },
  de: {
    cables_audio: 'Audiokabel',
    cables_power: 'Stromkabel',
    cables_network: 'Netzwerkkabel',
  },
};

const NETWORK_SIGNALS = new Set<Port['signal']>(['ethernet', 'dante', 'aes']);
const NETWORK_CONNECTORS = new Set<ConnectorType | undefined>(['ethercon', 'rj45']);

/** Cable category from the two ports: power, network (Ethernet/Dante/AES over Cat) or audio. */
export function cableCategory(a: Port | undefined, b: Port | undefined): CableNeed['category'] {
  const ports = [a, b].filter((p): p is Port => p !== undefined);
  if (ports.some((p) => p.signal === 'power')) return 'cables_power';
  if (ports.some((p) => NETWORK_SIGNALS.has(p.signal) || NETWORK_CONNECTORS.has(p.connector))) {
    return 'cables_network';
  }
  return 'cables_audio';
}

/** True if the link is radio (no cable). */
export function isWireless(a: Port | undefined, b: Port | undefined): boolean {
  return [a, b].some((p) => p?.signal === 'wireless' || p?.connector === 'wireless');
}

/** Cable name: the entered cable type, else from the connectors, else by category. */
export function cableName(
  category: CableNeed['category'],
  connectors: readonly (ConnectorType | undefined)[],
  locale: Locale,
  cableType?: string,
): string {
  const typed = cableType?.trim();
  if (typed !== undefined && typed !== '') return typed;
  for (const c of connectors) {
    const name = CABLE_NAMES[locale][c as ConnectorType];
    if (name !== undefined) return name;
  }
  return FALLBACK_NAMES[locale][category];
}

function label(item: Item | undefined, fallback: string): string {
  return item === undefined ? fallback : itemName(item);
}

/** First audio output of an item (what a channel source plugs in with). */
function sourcePort(item: Item, portId: string | undefined): Port | undefined {
  const ports = item.ports ?? [];
  return portId === undefined
    ? ports.find((p) => p.direction !== 'in' && p.signal !== 'power')
    : ports.find((p) => p.id === portId);
}

function touches(c: Connection, itemId: string, portId: string): boolean {
  return (
    (c.from.itemId === itemId && c.from.portId === portId) ||
    (c.to.itemId === itemId && c.to.portId === portId)
  );
}

/** True if the connection starts at a sub-core's fan-out output (part of the multicore). */
function isFanOut(data: BoardData, c: Connection): boolean {
  const item = data.items[c.from.itemId];
  const port = findPort(data, c.from.itemId, c.from.portId);
  return item?.kind === 'subcore' && port?.direction === 'out' && !isTrunk(port);
}

/**
 * Length of each sub-core's multicore: the longest of its fan-out runs (sub-core → stage box),
 * entered lengths winning. Sub-cores without fan-out connections are absent.
 */
export function subcoreLengths(
  data: BoardData,
  config: Pick<MaterialListConfig, 'cableAllowance' | 'stockLengths'>,
): Map<string, CableEstimate> {
  const out = new Map<string, CableEstimate>();
  for (const c of Object.values(data.connections)) {
    if (!isFanOut(data, c)) continue;
    const e = connectionLength(c, data.items[c.from.itemId], data.items[c.to.itemId], data, config);
    const prev = out.get(c.from.itemId);
    if (prev === undefined || (e.length ?? -1) > (prev.length ?? -1)) out.set(c.from.itemId, e);
  }
  return out;
}

function formalCables(data: BoardData, config: MaterialListConfig, locale: Locale): CableNeed[] {
  const needs: CableNeed[] = [];
  for (const c of Object.values(data.connections)) {
    const fromPort = findPort(data, c.from.itemId, c.from.portId);
    const toPort = findPort(data, c.to.itemId, c.to.portId);
    if (isWireless(fromPort, toPort) || isFanOut(data, c)) continue;
    const from = data.items[c.from.itemId];
    // A connection through a sub-core lane is cabled up to the sub-core.
    const to = data.items[c.via?.itemId ?? c.to.itemId];
    const category = cableCategory(fromPort, toPort);
    const estimate = connectionLength(c, from, to, data, config);
    needs.push({
      key: `conn:${c.id}`,
      category,
      name: cableName(category, [fromPort?.connector, toPort?.connector], locale, c.cableType),
      ...estimate,
      ends: [label(from, c.from.itemId), label(to, c.to.itemId)],
      sourceIds: [c.id],
      implicit: false,
    });
  }
  return needs;
}

/** Channels whose source item is cabled to its patch point only by the channel's patch text. */
function patchCables(data: BoardData, config: MaterialListConfig, locale: Locale): CableNeed[] {
  const connections = Object.values(data.connections);
  const needs: CableNeed[] = [];
  for (const ch of Object.values(data.channels)) {
    const source = data.items[ch.sourceItemId as string];
    if (source === undefined) continue;
    const out = sourcePort(source, ch.sourcePortId);
    if (out === undefined || isWireless(out, undefined)) continue;
    if (connections.some((c) => touches(c, source.id, out.id))) continue;
    const target = ch.patch === undefined ? undefined : resolvePatch(data, ch.patch);
    if (target === undefined) continue;
    const targetItem = data.items[target.itemId]!;
    const targetPort = findPort(data, target.itemId, target.portId)!;
    const category = cableCategory(out, targetPort);
    needs.push({
      key: `patch:${ch.id}`,
      category,
      name: cableName(category, [out.connector, targetPort.connector], locale),
      ...estimateCableLength(source, targetItem, data, config),
      ends: [itemName(source), itemName(targetItem)],
      sourceIds: [ch.id, source.id, target.itemId],
      implicit: true,
    });
  }
  return needs;
}

/** Devices assigned to a power source without a drawn power cable. */
function powerCables(data: BoardData, config: MaterialListConfig, locale: Locale): CableNeed[] {
  const connections = Object.values(data.connections);
  const needs: CableNeed[] = [];
  for (const item of Object.values(data.items)) {
    const sourceId = item.power?.sourceId;
    const source = data.power[sourceId as string];
    if (source === undefined) continue;
    const powerPorts = (item.ports ?? []).filter((p) => p.signal === 'power');
    if (powerPorts.some((p) => connections.some((c) => touches(c, item.id, p.id)))) continue;
    const distro = data.items[source.itemId as string];
    needs.push({
      key: `power:${item.id}`,
      category: 'cables_power',
      name: cableName(
        'cables_power',
        [source.plug === 'cee16' || source.plug === 'cee32' ? 'schuko' : source.plug],
        locale,
      ),
      ...estimateCableLength(item, distro, data, config),
      ends: [itemName(item), distro === undefined ? source.name : itemName(distro)],
      sourceIds: [item.id, source.id],
      implicit: true,
    });
  }
  return needs;
}

/**
 * Every cable the board needs: drawn connections (radio links and sub-core fan-outs excluded),
 * implicit audio links (channel source → patch text target) and implicit power links (device →
 * assigned power source). Ordered by key.
 */
export function cableNeeds(
  data: BoardData,
  config: MaterialListConfig,
  locale: Locale = 'en',
): CableNeed[] {
  return [
    ...formalCables(data, config, locale),
    ...patchCables(data, config, locale),
    ...powerCables(data, config, locale),
  ].sort((a, b) => a.key.localeCompare(b.key));
}
